import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, ArrowRight, Loader2 } from 'lucide-react';
import { API } from '../../config/site';
import { btn } from './shared';
import { OptionGroup, TextArea, TextField } from './formFields';

export const AGE_OPTIONS = ['20–24', '25–29', '30–34', '35–40', 'Anderes Alter'];
export const STAGE_OPTIONS = ['Ich investiere noch gar nicht', 'Ich habe einen ETF-Sparplan', 'Ich habe schon einzelne Aktien'];
export const BUDGET_OPTIONS = ['Unter 100 €', '100–300 €', '300–500 €', 'Über 500 €'];

const EMPTY = { first_name: '', email: '', phone: '', age: '', stage: '', budget: '', goal: '', privacy_accepted: false, website: '' };
const STEPS = [['first_name', 'email', 'phone'], ['age'], ['stage'], ['budget'], ['goal', 'privacy_accepted']];

const validate = (v) => {
  const e = {};
  if (!v.first_name.trim()) e.first_name = 'Bitte gib deinen Vornamen an.';
  if (!v.email.trim()) e.email = 'Bitte gib deine E-Mail-Adresse an.';
  else if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v.email.trim())) e.email = 'Das sieht nicht nach einer gültigen E-Mail-Adresse aus.';
  if (!v.phone.trim()) e.phone = 'Bitte gib deine Handynummer an, damit ich dich erreichen kann.';
  else if (!/^[0-9+()/\-\s]+$/.test(v.phone) || v.phone.replace(/\D/g, '').length < 6) e.phone = 'Bitte gib eine gültige Handynummer an.';
  if (!v.age) e.age = 'Bitte wähle dein Alter aus.';
  if (!v.stage) e.stage = 'Bitte wähle aus, wo du gerade stehst.';
  if (!v.budget) e.budget = 'Bitte wähle aus, wie viel du monatlich investieren kannst.';
  if (!v.goal.trim()) e.goal = 'Erzähl mir kurz, was du erreichen möchtest.';
  if (!v.privacy_accepted) e.privacy_accepted = 'Bitte bestätige die Datenschutzerklärung.';
  return e;
};

const Progress = ({ step, p }) => (
  <div data-testid={`apply-${p}step-indicator`}>
    <p className="text-xs font-semibold uppercase tracking-[0.14em] text-brand">Schritt {step + 1} von {STEPS.length}</p>
    <div className="mt-3 flex gap-1.5" aria-hidden="true">
      {STEPS.map((_, i) => <span key={i} className={`h-1.5 flex-1 rounded-full transition-colors duration-300 ${i <= step ? 'bg-brand' : 'bg-white/10'}`} />)}
    </div>
  </div>
);

export const ApplicationForm = ({ bare = false }) => {
  const navigate = useNavigate();
  const [values, setValues] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [step, setStep] = useState(0);
  const [sending, setSending] = useState(false);
  const [serverError, setServerError] = useState('');
  const isLast = step === STEPS.length - 1;
  const p = bare ? "dlg-" : "";

  const setVal = (key, val) => {
    setValues((p) => ({ ...p, [key]: val }));
    if (errors[key]) setErrors((p) => ({ ...p, [key]: undefined }));
  };
  const set = (key) => (ev) => setVal(key, ev.target.type === 'checkbox' ? ev.target.checked : ev.target.value);

  const stepErrors = () => {
    const all = validate(values);
    return Object.fromEntries(STEPS[step].filter((k) => all[k]).map((k) => [k, all[k]]));
  };

  const send = async () => {
    setSending(true);
    try {
      await axios.post(`${API}/apply`, values);
      sessionStorage.setItem('apply_first_name', values.first_name.trim());
      navigate('/danke-bewerbung', { state: { firstName: values.first_name.trim() } });
    } catch (err) {
      const d = err.response?.data?.detail;
      setServerError(typeof d === 'string' ? d : 'Da ist leider etwas schiefgelaufen. Bitte versuche es gleich noch einmal.');
      setSending(false);
    }
  };

  const submit = (ev) => {
    ev.preventDefault();
    setServerError('');
    const errs = stepErrors();
    setErrors(errs);
    const first = Object.keys(errs)[0];
    if (first) { document.getElementById(p + first)?.focus(); return; }
    if (isLast) send();
    else setStep((s) => s + 1);
  };

  return (
    <form onSubmit={submit} noValidate className={bare ? 'p-6 sm:p-8' : 'rounded-[28px] border border-line bg-navy p-6 shadow-[0_20px_60px_-30px_rgba(232,203,122,0.25)] sm:p-8'} data-testid={bare ? 'application-form-dialog' : 'application-form'}>
      <h3 className="font-display text-2xl font-bold text-white">Deine Bewerbung</h3>
      <div className="mt-5"><Progress step={step} p={p} /></div>
      <div key={step} className="step-in mt-7 min-h-[260px] space-y-5">
        {step === 0 && (
          <>
            <TextField id={`${p}first_name`} label="Vorname" autoComplete="given-name" value={values.first_name} onChange={set('first_name')} error={errors.first_name} />
            <TextField id={`${p}email`} label="E-Mail" type="email" autoComplete="email" inputMode="email" value={values.email} onChange={set('email')} error={errors.email} />
            <TextField id={`${p}phone`} label="Handynummer für WhatsApp oder Anruf" type="tel" autoComplete="tel" inputMode="tel" value={values.phone} onChange={set('phone')} error={errors.phone} />
            <div className="absolute -left-[9999px] h-px w-px overflow-hidden" aria-hidden="true">
              <label htmlFor={`${p}website`}>Website</label>
              <input id={`${p}website`} name="website" tabIndex={-1} autoComplete="off" value={values.website} onChange={set('website')} data-testid={`apply-${p}honeypot-input`} />
            </div>
          </>
        )}
        {step === 1 && <OptionGroup id={`${p}age`} label="Wie alt bist du?" options={AGE_OPTIONS} value={values.age} onChange={(v) => setVal('age', v)} error={errors.age} />}
        {step === 2 && <OptionGroup id={`${p}stage`} label="Wo stehst du gerade?" options={STAGE_OPTIONS} value={values.stage} onChange={(v) => setVal('stage', v)} error={errors.stage} />}
        {step === 3 && <OptionGroup id={`${p}budget`} label="Wie viel kannst du monatlich investieren?" options={BUDGET_OPTIONS} value={values.budget} onChange={(v) => setVal('budget', v)} error={errors.budget} />}
        {step === 4 && (
          <>
            <TextArea id={`${p}goal`} label="Was willst du in einem Jahr erreicht haben?" value={values.goal} onChange={set('goal')} error={errors.goal} />
            <div>
              <label className="flex cursor-pointer items-start gap-3 text-sm leading-relaxed text-ink">
                <input id={`${p}privacy_accepted`} type="checkbox" checked={values.privacy_accepted} onChange={set('privacy_accepted')}
                       className="mt-0.5 h-5 w-5 shrink-0 cursor-pointer rounded accent-brand" aria-invalid={!!errors.privacy_accepted}
                       aria-describedby={errors.privacy_accepted ? `${p}privacy_accepted-error` : undefined} data-testid={`apply-${p}privacy-checkbox`} />
                <span>
                  Ich habe die <a href="/datenschutz" target="_blank" rel="noopener noreferrer" className="font-semibold text-brand underline underline-offset-2">Datenschutzerklärung</a> gelesen und bin einverstanden, dass meine Angaben zur Bearbeitung meiner Bewerbung gespeichert werden.
                </span>
              </label>
              {errors.privacy_accepted && <p id={`${p}privacy_accepted-error`} className="mt-1.5 text-sm text-nope" data-testid={`${p}privacy_accepted-error`}>{errors.privacy_accepted}</p>}
            </div>
          </>
        )}
      </div>
      {serverError && <p role="alert" className="mt-5 rounded-2xl bg-nope/10 px-4 py-3 text-sm text-nope" data-testid={`apply-${p}server-error`}>{serverError}</p>}
      <div className="mt-6 flex gap-3">
        {step > 0 && (
          <button type="button" onClick={() => { setErrors({}); setStep((s) => s - 1); }} className={`${btn.outlineLight} px-5`} data-testid={`apply-${p}back-btn`} aria-label="Zurück">
            <ArrowLeft size={18} aria-hidden="true" />
          </button>
        )}
        {isLast ? (
          <button type="submit" disabled={sending} className={`${btn.primary} flex-1 disabled:cursor-not-allowed disabled:opacity-70`} data-testid={`apply-${p}submit-btn`}>
            {sending ? (<><Loader2 size={18} className="animate-spin" aria-hidden="true" /> Wird gesendet …</>) : 'Bewerbung absenden'}
          </button>
        ) : (
          <button type="submit" className={`${btn.primary} flex-1`} data-testid={`apply-${p}next-btn`}>
            Weiter <ArrowRight size={18} aria-hidden="true" />
          </button>
        )}
      </div>
      <p className="mt-3 text-center text-xs leading-relaxed text-ink">Kostenlos und unverbindlich. Deine Daten nutze ich nur, um mich bei dir zu melden.</p>
    </form>
  );
};
